import React, { useState, useMemo } from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isSameDay } from 'date-fns';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const InterviewCalendar = ({ 
  interviews, 
  onDateClick, 
  onInterviewClick, 
  viewType = 'week',
  selectedDate = new Date() 
}) => {
  const [currentDate, setCurrentDate] = useState(selectedDate);
  const [view, setView] = useState(viewType); // 'month', 'week', or 'day'
  
  // Calculate dates to display based on current view
  const calendarDays = useMemo(() => {
    let start, end;
    
    if (view === 'month') {
      start = startOfMonth(currentDate);
      end = endOfMonth(currentDate);
    } else if (view === 'week') {
      start = startOfWeek(currentDate, { weekStartsOn: 1 }); // Start on Monday
      end = endOfWeek(currentDate, { weekStartsOn: 1 });
    } else {
      // Day view just shows the current date
      return [currentDate];
    }
    
    return eachDayOfInterval({ start, end });
  }, [currentDate, view]);
  
  // Group interviews by date
  const interviewsByDate = useMemo(() => {
    const grouped = {};
    
    interviews.forEach(interview => {
      const date = new Date(interview.scheduled_at);
      const dateKey = format(date, 'yyyy-MM-dd');
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      
      grouped[dateKey].push(interview);
    });
    
    return grouped;
  }, [interviews]);
  
  // Navigation handlers
  const goToPrevious = () => {
    if (view === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    } else if (view === 'week') {
      setCurrentDate(new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000));
    } else {
      setCurrentDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000));
    }
  };
  
  const goToNext = () => {
    if (view === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    } else if (view === 'week') {
      setCurrentDate(new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000));
    } else {
      setCurrentDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000));
    }
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  return (
    <div className="bg-white border border-border rounded-lg overflow-hidden">
      {/* Calendar header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold text-xl">
            {view === 'month' && format(currentDate, 'MMMM yyyy')}
            {view === 'week' && `Week of ${format(calendarDays[0], 'MMM d')} - ${format(calendarDays[calendarDays.length - 1], 'MMM d, yyyy')}`}
            {view === 'day' && format(currentDate, 'EEEE, MMMM d, yyyy')}
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="ghost" onClick={goToPrevious}>
              <Icon name="ChevronLeft" size={16} />
            </Button>
            <Button variant="ghost" onClick={goToToday}>Today</Button>
            <Button variant="ghost" onClick={goToNext}>
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <Button 
            variant={view === 'month' ? 'secondary' : 'ghost'} 
            size="sm"
            onClick={() => setView('month')}
          >
            Month
          </Button>
          <Button 
            variant={view === 'week' ? 'secondary' : 'ghost'} 
            size="sm"
            onClick={() => setView('week')}
          >
            Week
          </Button>
          <Button 
            variant={view === 'day' ? 'secondary' : 'ghost'} 
            size="sm"
            onClick={() => setView('day')}
          >
            Day
          </Button>
        </div>
      </div>
      
      {/* Calendar grid */}
      <div className={`grid ${view === 'month' ? 'grid-cols-7' : view === 'week' ? 'grid-cols-7' : 'grid-cols-1'} divide-x divide-y divide-border`}>
        {/* Day headers for week and month views */}
        {(view === 'month' || view === 'week') && (
          <>
            <div className="p-2 text-center font-medium text-text-secondary bg-background">Mon</div>
            <div className="p-2 text-center font-medium text-text-secondary bg-background">Tue</div>
            <div className="p-2 text-center font-medium text-text-secondary bg-background">Wed</div>
            <div className="p-2 text-center font-medium text-text-secondary bg-background">Thu</div>
            <div className="p-2 text-center font-medium text-text-secondary bg-background">Fri</div>
            <div className="p-2 text-center font-medium text-text-secondary bg-background">Sat</div>
            <div className="p-2 text-center font-medium text-text-secondary bg-background">Sun</div>
          </>
        )}
        
        {/* Calendar cells */}
        {calendarDays.map(date => {
          const dateKey = format(date, 'yyyy-MM-dd');
          const dateInterviews = interviewsByDate[dateKey] || [];
          const isCurrentDay = isToday(date);
          
          return (
            <div 
              key={dateKey}
              className={`min-h-[100px] p-2 ${isCurrentDay ? 'bg-primary-50' : ''}`}
              onClick={() => onDateClick && onDateClick(date)}
            >
              <div className={`text-sm font-medium mb-2 ${isCurrentDay ? 'text-primary' : ''}`}>
                {format(date, 'd')}
              </div>
              
              <div className="space-y-1">
                {dateInterviews.slice(0, 3).map(interview => (
                  <div 
                    key={interview.id}
                    className={`px-2 py-1 rounded-md text-xs truncate cursor-pointer ${
                      interview.status === 'completed' ? 'bg-green-100 text-green-800' :
                      interview.status === 'cancelled' ? 'bg-gray-100 text-gray-600' :
                      'bg-primary-100 text-primary-800'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onInterviewClick && onInterviewClick(interview);
                    }}
                    title={`${format(new Date(interview.scheduled_at), 'h:mm a')} - ${interview.candidate_name}`}
                  >
                    {format(new Date(interview.scheduled_at), 'h:mm a')} - {interview.candidate_name}
                  </div>
                ))}
                
                {dateInterviews.length > 3 && view !== 'day' && (
                  <div className="text-xs text-text-secondary">
                    + {dateInterviews.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Daily schedule view (for day view) */}
      {view === 'day' && (
        <div className="p-4 space-y-4">
          {interviewsByDate[format(currentDate, 'yyyy-MM-dd')]?.map(interview => (
            <div 
              key={interview.id}
              className="border-l-4 border-primary p-3 rounded-md bg-background hover:bg-primary-50 cursor-pointer"
              onClick={() => onInterviewClick && onInterviewClick(interview)}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium">{format(new Date(interview.scheduled_at), 'h:mm a')}</span>
                <span className={`text-sm px-2 py-0.5 rounded-full ${
                  interview.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                  interview.status === 'completed' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {interview.status}
                </span>
              </div>
              <div className="font-medium">{interview.candidate_name}</div>
              <div className="text-sm text-text-secondary">{interview.job_title}</div>
              <div className="text-sm mt-1 flex items-center gap-2">
                <Icon name={interview.type === 'video' ? 'Video' : interview.type === 'phone' ? 'Phone' : 'MapPin'} size={14} />
                <span>{interview.type === 'video' ? 'Video Call' : interview.type === 'phone' ? 'Phone Call' : 'In-Person'}</span>
              </div>
            </div>
          ))}
          
          {(!interviewsByDate[format(currentDate, 'yyyy-MM-dd')] || interviewsByDate[format(currentDate, 'yyyy-MM-dd')].length === 0) && (
            <div className="text-center py-8 text-text-muted">
              No interviews scheduled for this day
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InterviewCalendar;